import { getContractorIdsFromPrices, priceQueryOptions } from '@price/queries/priceQueries';
import { queryClient } from '@store/queryClient';
import { store } from '@store';

export type ContractorResolution =
    | { kind: 'resolved'; contractorId?: string }
    | { kind: 'selectionRequired'; contractorIds: string[] };

export async function resolveContractor(
    serviceId: string,
    legalEntityId?: string,
    errorMessage?: string,
): Promise<ContractorResolution | undefined> {
    const contractorIds = await loadContractorIdsOrShowError(serviceId, legalEntityId, errorMessage);

    if (!contractorIds) {
        return undefined;
    }

    return contractorIds.length > 1
        ? { kind: 'selectionRequired', contractorIds }
        : { kind: 'resolved', contractorId: contractorIds[0] };
}

export async function prefetchContractorPrices(serviceIds: string[], legalEntityId?: string): Promise<void> {
    if (!legalEntityId) {
        return;
    }

    await Promise.all(
        serviceIds.map(serviceId =>
            queryClient.prefetchQuery(priceQueryOptions.contractorPrices(serviceId, legalEntityId)),
        ),
    );
}

export function getCachedContractorIds(serviceId: string, legalEntityId?: string): string[] | undefined {
    if (!legalEntityId) {
        return [];
    }

    const prices = queryClient.getQueryData(priceQueryOptions.contractorPrices(serviceId, legalEntityId).queryKey);

    return prices ? getContractorIdsFromPrices(prices) : undefined;
}

export function isStoredContractorAvailable(storedContractorId: string | undefined, contractorIds: string[]): boolean {
    return !!storedContractorId && contractorIds.includes(storedContractorId);
}

async function loadContractorIdsOrShowError(
    serviceId: string,
    legalEntityId?: string,
    errorMessage?: string,
): Promise<string[] | undefined> {
    const { app } = store;

    return app.doOrShowError({
        action: () => loadContractorIds(serviceId, legalEntityId),
        errorMessage,
    });
}

async function loadContractorIds(serviceId: string, legalEntityId?: string): Promise<string[]> {
    if (!legalEntityId) {
        return [];
    }

    const prices = await queryClient.fetchQuery(priceQueryOptions.contractorPrices(serviceId, legalEntityId));

    return getContractorIdsFromPrices(prices);
}
